import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Menu, X, ChevronRight, Shield, Lock, FileText,
  Search, MessageSquare, Users, Bell, BarChart3,
  CheckCircle2, Star
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  // GSAP animation refs
  const navRef = useRef(null);
  const badgeRef = useRef(null);
  const h1Ref = useRef(null);
  const subtitleRef = useRef(null);
  const ctaRef = useRef(null);
  const ctaPrimaryRef = useRef(null);
  const ctaGlowRef = useRef(null);
  const trustBadgesRef = useRef(null);
  const imageRef = useRef(null);
  const heroRef = useRef(null);
  const heroLeftRef = useRef(null);
  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);
  const orb3Ref = useRef(null);
  const socialProofRef = useRef(null);
  const featureCardsRef = useRef(null);
  const diffRef = useRef(null);
  const solutionCardsRef = useRef(null);
  const testimonialCardsRef = useRef(null);
  const pricingCardsRef = useRef(null);
  const ctaSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const hero = heroRef.current;
    let onMouseMove = null;
    let onHeroResize = null;

    const ctx = gsap.context(() => {
      // Navbar entrance
      gsap.from(navRef.current, { autoAlpha: 0, y: -20, duration: 0.4, ease: 'power2.out' });

      // Hero timeline: badge → H1 → subtitle → CTAs → trust → image
      const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
      tl.from(badgeRef.current,                  { autoAlpha: 0, y: 30, duration: 0.5 })
        .from(h1Ref.current,                     { autoAlpha: 0, y: 55, skewY: 1.5, duration: 0.7 }, '-=0.2')
        .from(subtitleRef.current,               { autoAlpha: 0, y: 30, duration: 0.5 }, '-=0.3')
        .from(ctaRef.current,                    { autoAlpha: 0, scale: 0.93, duration: 0.5, ease: 'back.out(1.7)' }, '-=0.2')
        .from(trustBadgesRef.current.children,   { autoAlpha: 0, x: -15, stagger: 0.1, duration: 0.4 }, '-=0.1')
        .from(imageRef.current,                  { autoAlpha: 0, x: 80, scale: 0.92, duration: 0.9 }, '<-0.9')
        .to(imageRef.current,  { y: -14, duration: 3,   ease: 'sine.inOut', yoyo: true, repeat: -1 }, '+=0.1')
        .to(ctaGlowRef.current, { opacity: 0.9, duration: 1.8, ease: 'sine.inOut', yoyo: true, repeat: -1 }, '<');

      // Ambient orbs — infinite gentle oscillation
      gsap.to(orb1Ref.current, { x: 40,  y: 30,  duration: 7,  ease: 'sine.inOut', yoyo: true, repeat: -1 });
      gsap.to(orb2Ref.current, { x: -35, y: -25, duration: 9,  ease: 'sine.inOut', yoyo: true, repeat: -1 });
      gsap.to(orb3Ref.current, { x: 20,  y: -30, duration: 11, ease: 'sine.inOut', yoyo: true, repeat: -1 });

      // Mouse parallax — quickTo reuses a single tween per property, zero new-tween overhead on every move
      const imageXTo    = gsap.quickTo(imageRef.current,    'x', { duration: 0.8, ease: 'power3.out' });
      const heroLeftXTo = gsap.quickTo(heroLeftRef.current, 'x', { duration: 1.0, ease: 'power3.out' });

      // Cache rect — getBoundingClientRect() in mousemove forces sync layout; cache and refresh on resize only
      let heroRect = hero.getBoundingClientRect();
      onHeroResize = () => { heroRect = hero.getBoundingClientRect(); };
      window.addEventListener('resize', onHeroResize, { passive: true });

      onMouseMove = (e) => {
        const nx = (e.clientX - heroRect.left - heroRect.width  / 2) / (heroRect.width  / 2);
        imageXTo(nx * 18);
        heroLeftXTo(-nx * 6);
      };
      hero.addEventListener('mousemove', onMouseMove);

      // ScrollTrigger scroll-in animations
      gsap.from(socialProofRef.current.querySelectorAll('div > span'), {
        y: 15, stagger: 0.08, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: socialProofRef.current, start: 'top 80%', once: true },
      });
      gsap.from(featureCardsRef.current.children, {
        y: 30, stagger: 0.07, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: featureCardsRef.current, start: 'top 80%', once: true },
      });
      diffRef.current.querySelectorAll('[data-count]').forEach(el => {
        const target = parseFloat(el.dataset.count);
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target, duration: 1.5, ease: 'power1.out',
          // Keep the real value visible until the count-up actually runs, so the
          // number never gets stuck at 0 if the section starts below the fold.
          immediateRender: false,
          onUpdate: () => { el.textContent = Math.round(obj.val); },
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        });
      });
      gsap.from(solutionCardsRef.current.children, {
        y: 30, stagger: 0.1, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: solutionCardsRef.current, start: 'top 80%', once: true },
      });
      gsap.from(testimonialCardsRef.current.children, {
        x: -20, stagger: 0.12, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: testimonialCardsRef.current, start: 'top 80%', once: true },
      });
      gsap.from(pricingCardsRef.current.children, {
        y: 30, stagger: 0.1, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: pricingCardsRef.current, start: 'top 80%', once: true },
      });
      gsap.from(ctaSectionRef.current.querySelector('h2'), {
        scale: 0.95, duration: 0.6, ease: 'power2.out',
        scrollTrigger: { trigger: ctaSectionRef.current, start: 'top 80%', once: true },
      });
    });

    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad, { once: true });

    return () => {
      ctx.revert();
      if (onMouseMove) hero.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('load', onLoad);
      window.removeEventListener('resize', onHeroResize);
    };
  }, []);

  return (
    <div className="landing-dark" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--foreground)', minHeight: '100vh' }}>

      {/* === NAVBAR === */}
      <nav
        ref={navRef}
        className="fixed top-0 w-full z-50"
        style={{
          backgroundColor: 'rgba(5, 5, 6, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: navScrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
          transition: 'border-color 300ms ease',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>
                Lawliet<span style={{ color: 'var(--primary)' }}>.ai</span>
              </span>
            </div>

            <div className="hidden md:flex items-center">
              <div className="flex items-center space-x-8 mr-12">
                {['#features', '#solutions', '#pricing', '#testimonials'].map((href, i) => (
                  <a
                    key={href}
                    href={href}
                    className="text-sm transition-colors duration-200"
                    style={{ color: 'var(--foreground-muted)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--foreground)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--foreground-muted)')}
                  >
                    {['Fonctionnalités', 'Solutions', 'Tarifs', 'Clients'][i]}
                  </a>
                ))}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/auth')}
                  className="px-4 py-2 text-sm rounded-lg transition-all duration-200"
                  style={{ color: 'var(--foreground-muted)', border: '1px solid rgba(255,255,255,0.12)' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = 'var(--foreground)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = 'var(--foreground-muted)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                  }}
                >
                  Connexion
                </button>
                <button
                  onClick={() => setShowDemoForm(true)}
                  className="px-5 py-2 text-sm font-medium rounded-lg text-white transition-colors duration-200"
                  style={{ backgroundColor: 'var(--primary)' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2563EB')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--primary)')}
                >
                  Demander une démo
                </button>
              </div>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2.5"
              aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              style={{ color: 'var(--foreground)' }}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className="md:hidden border-t"
            style={{ backgroundColor: 'var(--bg-base)', borderColor: 'var(--border)' }}
          >
            <div className="px-4 py-4 space-y-3">
              {['#features', '#solutions', '#pricing', '#testimonials'].map((href, i) => (
                <a key={href} href={href} className="block text-sm" style={{ color: 'var(--foreground-muted)' }}>
                  {['Fonctionnalités', 'Solutions', 'Tarifs', 'Clients'][i]}
                </a>
              ))}
              <button
                onClick={() => navigate('/auth')}
                className="block w-full text-left text-sm py-1"
                style={{ color: 'var(--foreground-muted)' }}
              >
                Connexion
              </button>
              <button
                onClick={() => setShowDemoForm(true)}
                className="block w-full py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                Demander une démo
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* === HERO === */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8"
        style={{ background: 'radial-gradient(ellipse at center, var(--bg-elevated) 0%, var(--bg-deep) 100%)' }}
      >
        {/* Ambient orb — blue */}
        <div
          ref={orb1Ref}
          className="absolute pointer-events-none"
          style={{
            top: '-80px', left: '-80px',
            width: '500px', height: '500px',
            background: 'rgba(59,130,246,0.13)',
            borderRadius: '50%',
            filter: 'blur(90px)',
            willChange: 'transform',
          }}
        />
        {/* Ambient orb — gold */}
        <div
          ref={orb2Ref}
          className="absolute pointer-events-none"
          style={{
            bottom: '-60px', right: '-60px',
            width: '380px', height: '380px',
            background: 'rgba(245,158,11,0.09)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            willChange: 'transform',
          }}
        />
        {/* Ambient orb — violet (centre) */}
        <div
          ref={orb3Ref}
          className="absolute pointer-events-none"
          style={{
            top: '40%', left: '45%',
            transform: 'translate(-50%, -50%)',
            width: '320px', height: '320px',
            background: 'rgba(139,92,246,0.07)',
            borderRadius: '50%',
            filter: 'blur(70px)',
            willChange: 'transform',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div ref={heroLeftRef} className="space-y-6 sm:space-y-8" style={{ willChange: 'transform' }}>
              {/* Badge */}
              <div
                ref={badgeRef}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  border: '1px solid rgba(245,158,11,0.3)',
                  backgroundColor: 'rgba(245,158,11,0.08)',
                  color: 'var(--accent-gold)',
                }}
              >
                <Shield className="h-4 w-4" />
                <span>Hébergé en France • Conforme RGPD</span>
              </div>

              {/* H1 */}
              <h1
                ref={h1Ref}
                className="text-4xl sm:text-5xl lg:text-6xl leading-tight"
                style={{
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontWeight: 600,
                  color: 'var(--foreground)',
                }}
              >
                Intelligence Juridique
                <br />
                <span style={{ color: 'var(--primary)' }}>Made in France</span>
              </h1>

              {/* Subtitle */}
              <p
                ref={subtitleRef}
                className="text-base sm:text-xl leading-relaxed"
                style={{ color: 'var(--foreground-muted)' }}
              >
                La plateforme IA pour les professionnels du droit français et européen.
                Analysez des documents, rédigez des contrats, recherchez la jurisprudence —
                avec transparence totale, conformité RGPD et données hébergées en France.
              </p>

              {/* CTAs */}
              <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4">
                <button
                  ref={ctaPrimaryRef}
                  onClick={() => setShowDemoForm(true)}
                  className="relative px-8 py-4 text-white font-semibold rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2 group"
                  style={{ backgroundColor: 'var(--primary)' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2563EB')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--primary)')}
                >
                  <span
                    ref={ctaGlowRef}
                    aria-hidden="true"
                    style={{
                      position: 'absolute', inset: 0, borderRadius: '0.75rem',
                      boxShadow: '0 0 28px rgba(59,130,246,0.7), 0 0 60px rgba(59,130,246,0.3)',
                      opacity: 0, pointerEvents: 'none',
                    }}
                  />
                  <span>Demander une démo</span>
                  <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/auth')}
                  className="px-8 py-4 font-semibold rounded-xl transition-all duration-200"
                  style={{
                    color: 'var(--foreground)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)')}
                >
                  Accéder à la plateforme
                </button>
              </div>

              {/* Trust badges */}
              <div
                ref={trustBadgesRef}
                className="flex flex-wrap items-center gap-6 pt-6"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                {['Conforme RGPD', 'ISO 27001', 'Hébergé en France'].map(label => (
                  <div
                    key={label}
                    className="flex items-center space-x-2 text-sm"
                    style={{ color: 'var(--foreground-muted)' }}
                  >
                    <CheckCircle2 className="h-5 w-5" style={{ color: 'var(--primary)' }} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero image */}
            <div ref={imageRef} className="relative flex justify-center" style={{ willChange: 'transform' }}>
              <img
                src="/lawliet-mascot.png"
                alt="Lawliet AI Assistant"
                className="w-full max-w-md drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* === SOCIAL PROOF === */}
      <section
        className="py-8 sm:py-12"
        style={{
          backgroundColor: 'var(--bg-base)',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div ref={socialProofRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p
            className="text-center text-xs font-semibold uppercase tracking-widest mb-4 sm:mb-6"
            style={{ color: 'var(--foreground-muted)' }}
          >
            Conçu pour les professionnels du droit français et européen
          </p>
          <div className="flex flex-wrap justify-center items-center gap-3">
            {["Avocats", "Juristes d'entreprise", "Notaires", "Cabinets", "Équipes juridiques", "Stagiaires"].map(label => (
              <span
                key={label}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: 'var(--foreground)',
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* === FEATURES === */}
      <section
        id="features"
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 scroll-mt-16"
        style={{ backgroundColor: 'var(--bg-elevated)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              Une plateforme, tous vos workflows juridiques
            </h2>
            <p className="text-base sm:text-xl max-w-3xl mx-auto" style={{ color: 'var(--foreground-muted)' }}>
              De l'analyse de documents à la rédaction de contrats, de la recherche à la veille
              réglementaire — Lawliet.ai accompagne chaque aspect du travail juridique moderne.
            </p>
          </div>

          <div ref={featureCardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageSquare, title: 'Assistant IA Juridique', description: "Posez des questions complexes en langage naturel. Obtenez des réponses précises avec citations vérifiées du Code civil, RGPD et jurisprudence." },
              { icon: FileText,      title: 'Analyse de Documents',  description: "Uploadez contrats, jugements ou documents juridiques. Extrayez instantanément les clauses clés, identifiez les risques et générez des résumés." },
              { icon: Search,        title: 'Recherche Sémantique',  description: "Recherchez dans toute votre base documentaire grâce à la compréhension sémantique par IA. Trouvez les précédents pertinents en secondes." },
              { icon: FileText,      title: 'Rédaction de Contrats', description: "Générez des contrats juridiquement solides adaptés au droit français et européen. Choisissez parmi des templates ou créez des documents sur mesure." },
              { icon: Bell,          title: 'Veille Légale',          description: "Anticipez les évolutions avec une surveillance automatisée des nouvelles lois, règlements et jurisprudence. Alertes personnalisées et analyse d'impact." },
              { icon: Users,         title: 'Outils Collaboratifs',   description: "Travaillez ensemble sur les documents avec versionnage, annotations et workflows de validation. Idéal pour les équipes et cabinets." },
              { icon: BarChart3,     title: 'Traduction Juridique',   description: "Traduisez vos textes et documents dans 13 langues grâce à DeepL. Mise en forme originale préservée pour les documents Word et PDF." },
              { icon: Lock,          title: 'Conformité RGPD',        description: "Protection des données intégrée, chiffrement, journaux d'audit et droit à l'oubli. Les données de vos clients restent en France." },
              { icon: Shield,        title: 'Transparence Totale',    description: "Chaque réponse IA inclut les sources, les étapes de raisonnement et les scores de confiance. Exportez les rapports d'audit à tout moment." },
            ].map(({ icon: Icon, title, description }) => (
              <FeatureCard
                key={title}
                icon={<Icon className="h-6 w-6" style={{ color: 'var(--primary)' }} />}
                title={title}
                description={description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* === DIFFERENTIATORS === */}
      <section
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: 'var(--bg-deep)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              Pourquoi choisir Lawliet.ai ?
            </h2>
            <p className="text-base sm:text-xl max-w-3xl mx-auto" style={{ color: 'var(--foreground-muted)' }}>
              La seule plateforme IA juridique conçue spécifiquement pour les données européennes et la transparence
            </p>
          </div>
          <div ref={diffRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { stat: '99',  suffix: '%', title: 'IA Juridique Avancée',   description: "Propulsé par des modèles de pointe. Analyse juridique de précision et génération intelligente." },
              { stat: '100', suffix: '%', title: 'Hébergé en France',      description: "Vos données ne quittent jamais la France. Hébergé sur infrastructure Scaleway et OVH, datacenters Paris et Roubaix." },
              { stat: '13',  suffix: '',  title: 'Langues couvertes',      description: "Traduction professionnelle avec DeepL. Mise en forme originale préservée pour tous les documents." },
              { stat: '24',  suffix: 'h', title: 'Explicabilité Complète', description: "Voyez exactement comment l'IA arrive à ses conclusions. Exportez les traces d'audit pour chaque décision." },
            ].map(({ stat, suffix, title, description }) => (
              <DifferentiatorCard key={title} stat={stat} suffix={suffix} title={title} description={description} />
            ))}
          </div>
        </div>
      </section>

      {/* === SOLUTIONS === */}
      <section
        id="solutions"
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 scroll-mt-16"
        style={{ backgroundColor: 'var(--bg-elevated)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              Une solution pour chaque professionnel du droit
            </h2>
          </div>
          <div ref={solutionCardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SolutionCard
              color="#3B82F6"
              title="Cabinets d'avocats"
              description="Démultipliez votre expertise. Accélérez la due diligence, la revue contractuelle et la recherche juridique tout en maintenant qualité et précision."
              features={["Gestion multi-cabinets", "Organisation par dossier client", "Suivi du temps facturable", "Outils de collaboration d'équipe"]}
            />
            <SolutionCard
              color="#6366F1"
              title="Juristes d'entreprise"
              description="Traitez efficacement les volumes importants. Concentrez-vous sur le conseil stratégique plutôt que les tâches répétitives."
              features={["Gestion du portefeuille contractuel", "Automatisation de l'évaluation des risques", "Surveillance de la conformité réglementaire", "Collaboration inter-fonctionnelle"]}
            />
            <SolutionCard
              color="#8B5CF6"
              title="Avocats indépendants"
              description="Concurrencez les grands cabinets grâce à l'IA. Livrez des résultats plus rapides et de meilleure qualité pour développer votre activité."
              features={["Plans abordables pour solo", "Automatisation documentaire", "Outils de communication client", "Templates par domaine de droit"]}
            />
            <SolutionCard
              color="#10B981"
              title="Directions juridiques"
              description="Dotez toute votre organisation d'intelligence juridique. Permettez aux équipes métier de s'autonomiser sur les sujets courants."
              features={["Déploiement entreprise", "Automatisation de workflows sur mesure", "Analytics avancés", "Authentification SSO"]}
            />
          </div>
        </div>
      </section>

      {/* === TESTIMONIALS === */}
      <section
        id="testimonials"
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 scroll-mt-16"
        style={{ backgroundColor: 'var(--bg-base)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              La confiance des innovateurs du droit
            </h2>
          </div>
          <div ref={testimonialCardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <TestimonialCard
              quote="Lawliet.ai a transformé notre processus de revue contractuelle. Ce qui prenait des heures prend maintenant des minutes, avec une précision accrue et des traces d'audit complètes."
              author="Marie Dubois" role="Directrice Juridique" company="TechEurope SAS"
            />
            <TestimonialCard
              quote="En tant qu'avocat indépendant, Lawliet me donne les capacités d'un grand cabinet. L'hébergement des données en France était non négociable pour mes clients."
              author="Jean-Pierre Martin" role="Avocat" company="Cabinet Martin & Associés"
            />
            <TestimonialCard
              quote="Les fonctionnalités de transparence et d'explicabilité sont révolutionnaires. Je peux présenter sereinement les travaux assistés par IA à mes clients et aux juridictions."
              author="Sophie Laurent" role="Associée" company="Laurent Legal Partners"
            />
          </div>
        </div>
      </section>

      {/* === PRICING === */}
      <section
        id="pricing"
        className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 scroll-mt-16"
        style={{ backgroundColor: 'var(--bg-elevated)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              Tarifs transparents pour chaque pratique
            </h2>
            <p className="text-base sm:text-xl" style={{ color: 'var(--foreground-muted)' }}>
              Choisissez le plan adapté à vos besoins. Sans frais cachés, résiliable à tout moment.
            </p>
          </div>
          <div ref={pricingCardsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard
              name="Solo" price="€99" period="par mois"
              description="Idéal pour les praticiens indépendants et petites équipes"
              features={["1 utilisateur", "5 000 requêtes IA/mois", "100 Go de stockage", "Toutes les fonctionnalités core", "Support email", "Conforme RGPD"]}
              cta="Essai gratuit" onCTA={() => setShowDemoForm(true)}
            />
            <PricingCard
              name="Cabinet" price="€299" period="par mois"
              description="Pour les cabinets d'avocats et directions juridiques"
              features={["Jusqu'à 10 utilisateurs", "25 000 requêtes IA/mois", "500 Go de stockage", "Toutes les fonctionnalités + collaboration", "Support prioritaire", "Workflows personnalisés", "Analytics d'équipe"]}
              cta="Demander une démo" onCTA={() => setShowDemoForm(true)} popular={true}
            />
            <PricingCard
              name="Enterprise" price="Sur devis" period="tarification"
              description="Pour les grandes organisations avec des besoins avancés"
              features={["Utilisateurs illimités", "Requêtes IA illimitées", "Stockage illimité", "Infrastructure dédiée", "Support 24/7", "Intégrations sur mesure", "Garantie SLA", "Options on-premise"]}
              cta="Contacter les ventes" onCTA={() => setShowDemoForm(true)}
            />
          </div>
        </div>
      </section>

      {/* === FINAL CTA === */}
      <section
        ref={ctaSectionRef}
        className="relative overflow-hidden py-24 px-4 sm:px-6 lg:px-8"
        style={{ background: 'linear-gradient(135deg, var(--bg-deep) 0%, var(--bg-elevated) 100%)' }}
      >
        <div
          className="absolute pointer-events-none"
          style={{
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '500px', height: '500px',
            background: 'rgba(59,130,246,0.08)',
            borderRadius: '50%',
            filter: 'blur(100px)',
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6"
            style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
          >
            Prêt à transformer votre pratique juridique ?
          </h2>
          <p className="text-base sm:text-xl mb-8" style={{ color: 'var(--foreground-muted)' }}>
            Rejoignez la révolution de l'IA juridique européenne. Planifiez une démo personnalisée.
          </p>
          <button
            onClick={() => setShowDemoForm(true)}
            className="px-8 py-4 font-semibold rounded-xl text-white transition-colors duration-200 inline-flex items-center space-x-2"
            style={{ backgroundColor: 'var(--primary)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2563EB')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--primary)')}
          >
            <span>Demander une démo</span>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer
        className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: 'var(--bg-deep)', borderTop: '1px solid var(--border)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-3">
                <span className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>
                  Lawliet<span style={{ color: 'var(--primary)' }}>.ai</span>
                </span>
              </div>
              <p className="text-sm max-w-md" style={{ color: 'var(--foreground-muted)' }}>
                Intelligence juridique européenne pour les professionnels du droit
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {[['#features', 'Fonctionnalités'], ['#solutions', 'Solutions'], ['#pricing', 'Tarifs'], ['#testimonials', 'Témoignages']].map(([href, label]) => (
                <a
                  key={href} href={href}
                  className="transition-colors duration-200"
                  style={{ color: 'var(--foreground-muted)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--primary)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--foreground-muted)')}
                >
                  {label}
                </a>
              ))}
            </div>
            <div className="text-sm text-center md:text-right" style={{ color: 'var(--foreground-muted)' }}>
              <p>&copy; {new Date().getFullYear()} Lawliet.ai</p>
              <p className="mt-1">Made with ⚖️ in France</p>
            </div>
          </div>
        </div>
      </footer>

      {showDemoForm && <DemoModal onClose={() => setShowDemoForm(false)} />}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const FeatureCard = ({ icon, title, description }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="p-6 rounded-2xl"
      style={{
        backgroundColor: hovered ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${hovered ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.08)'}`,
        transition: 'background-color 200ms ease, border-color 200ms ease',
      }}
    >
      <div
        className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
        style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--foreground)' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground-muted)' }}>{description}</p>
    </div>
  );
};

const DifferentiatorCard = ({ stat, suffix, title, description }) => (
  <div className="text-center p-6">
    <div className="text-4xl sm:text-5xl font-bold mb-1" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--primary)' }}>
      <span data-count={stat}>{stat}</span>
      <span className="text-2xl">{suffix}</span>
    </div>
    <h3
      className="text-xl font-semibold mb-3"
      style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
    >
      {title}
    </h3>
    <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>{description}</p>
  </div>
);

const SolutionCard = ({ color, title, description, features }) => (
    <div
      onMouseEnter={(e) => gsap.to(e.currentTarget, { y: -4, duration: 0.2, ease: 'power2.out' })}
      onMouseLeave={(e) => gsap.to(e.currentTarget, { y: 0,  duration: 0.2, ease: 'power2.in'  })}
      className="p-6 sm:p-8 rounded-2xl"
      style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderTop: `2px solid ${color}`,
      }}
    >
      <h3
        className="text-xl sm:text-2xl font-semibold mb-4"
        style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
      >
        {title}
      </h3>
      <p className="mb-6 text-sm leading-relaxed" style={{ color: 'var(--foreground-muted)' }}>{description}</p>
      <ul className="space-y-3">
        {features.map(feature => (
          <li key={feature} className="flex items-start space-x-3">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color }} />
            <span className="text-sm" style={{ color: 'var(--foreground)' }}>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
);

const TestimonialCard = ({ quote, author, role, company }) => (
  <div
    className="p-6 sm:p-8 rounded-2xl"
    style={{
      backgroundColor: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <div className="flex mb-4">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="h-5 w-5 fill-current" style={{ color: 'var(--accent-gold)' }} />
      ))}
    </div>
    <p className="text-sm leading-relaxed mb-6 italic" style={{ color: 'var(--foreground-muted)' }}>
      "{quote}"
    </p>
    <div>
      <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{author}</p>
      <p className="text-xs mt-0.5" style={{ color: 'var(--foreground-muted)' }}>{role}</p>
      <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{company}</p>
    </div>
  </div>
);

const PricingCard = ({ name, price, period, description, features, cta, onCTA, popular }) => {
  return (
    <div
      className="p-6 sm:p-8 rounded-2xl relative"
      style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: `1px solid ${popular ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.08)'}`,
        boxShadow: popular ? '0 0 30px rgba(59,130,246,0.15)' : 'none',
      }}
    >
      {popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span
            className="px-4 py-1 text-xs font-semibold rounded-full text-white"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            Populaire
          </span>
        </div>
      )}
      <h3
        className="text-xl sm:text-2xl font-semibold mb-2"
        style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
      >
        {name}
      </h3>
      <div className="mb-4">
        <span className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>{price}</span>
        <span className="text-sm ml-1" style={{ color: 'var(--foreground-muted)' }}>/ {period}</span>
      </div>
      <p className="text-sm mb-6" style={{ color: 'var(--foreground-muted)' }}>{description}</p>
      <ul className="space-y-3 mb-8">
        {features.map(feature => (
          <li key={feature} className="flex items-start space-x-3">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
            <span className="text-sm" style={{ color: 'var(--foreground)' }}>{feature}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onCTA}
        className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200"
        style={{
          backgroundColor: popular ? 'var(--primary)' : 'rgba(255,255,255,0.08)',
          color: popular ? 'white' : 'var(--foreground)',
          border: popular ? 'none' : '1px solid rgba(255,255,255,0.12)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = popular ? '#2563EB' : 'rgba(255,255,255,0.12)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = popular ? 'var(--primary)' : 'rgba(255,255,255,0.08)';
        }}
      >
        {cta}
      </button>
    </div>
  );
};

const DemoModal = ({ onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', organization: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(onClose, 2000);
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        className="rounded-2xl max-w-md w-full p-6 sm:p-8 relative"
        style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 p-2 rounded-lg transition-colors"
          style={{ color: 'var(--foreground-muted)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--foreground)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--foreground-muted)')}
        >
          <X className="h-5 w-5" />
        </button>

        {submitSuccess ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--primary)' }} />
            <h3
              className="text-xl sm:text-2xl font-semibold mb-2"
              style={{ color: 'var(--foreground)' }}
            >
              Merci !
            </h3>
            <p style={{ color: 'var(--foreground-muted)' }}>
              Nous vous contacterons rapidement pour planifier votre démo.
            </p>
          </div>
        ) : (
          <>
            <h3
              id="demo-modal-title"
              className="text-xl sm:text-2xl font-semibold mb-2"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", color: 'var(--foreground)' }}
            >
              Demander une démo
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--foreground-muted)' }}>
              Découvrez comment Lawliet.ai peut transformer votre pratique juridique
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                { label: 'Nom complet *',    field: 'name',         type: 'text'  },
                { label: 'Adresse email *',  field: 'email',        type: 'email' },
                { label: 'Organisation *',   field: 'organization', type: 'text'  },
              ].map(({ label, field, type }) => (
                <div key={field}>
                  <label
                    htmlFor={field}
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: 'var(--foreground-muted)' }}
                  >
                    {label}
                  </label>
                  <input
                    id={field}
                    type={type}
                    required
                    value={formData[field]}
                    onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'var(--foreground)',
                    }}
                  />
                </div>
              ))}
              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: 'var(--foreground-muted)' }}
                >
                  Message (optionnel)
                </label>
                <textarea
                  rows={3}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Décrivez vos besoins..."
                  className="w-full px-4 py-2.5 rounded-lg text-sm outline-none resize-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'var(--foreground)',
                  }}
                />
              </div>
              {submitError && (
                <p className="text-xs text-center" style={{ color: '#F87171' }}>
                  Une erreur est survenue. Veuillez réessayer.
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--primary)' }}
                onMouseEnter={e => !isSubmitting && (e.currentTarget.style.backgroundColor = '#2563EB')}
                onMouseLeave={e => !isSubmitting && (e.currentTarget.style.backgroundColor = 'var(--primary)')}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Demander une démo'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Landing;
